import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/styles';
import { Tab, Tabs, Tooltip } from '@material-ui/core';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import clsx from 'clsx';

import styles, { CUSTOM_STYLES } from '../styles/subscriptionStep';
import { actions as reduxActions } from '../reducers';
import { getData } from '../../../utils/http';
import { URLS } from '../../../consts/endpoints';
import showSnackbar from '../../../utils/showSnackbar';
import InputRow from '../../../partials/dataRows/InputRow';
import CheckboxRow from '../../../partials/dataRows/CheckboxRow';
import {
  isStringDefined,
  doesArrayHasLength,
  isDefined,
  isUndefined,
  isStringEmpty
} from '../../../utils/isDefined';
import validateSchema from '../actions/validateSchema';
import { getAllSubsciptions } from '../actions/getSupscriptions';
import Optional from '../../../utils/optional';
import SelectRow from '../../../partials/dataRows/SelectRow';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import { PROVIDERS_NUMBERS } from '../../../consts/initialValues';
import MandatoryField from './MandatoryField';
import ValidateEuicc from './ValidateEuicc';
import { translate } from '../../../i18n/I18nProvider';
import mapTemplateCharacteristic from '../actions/mapTemplateCharacteristic';
import { findProductPrices } from '../../../pages/simCardsIoT/actions/customActivateSimcardActions';

export const SubscriptionStep = ({
  classes,
  resestUserWizard,
  handleBack,
  userData,
  setUserData,
  getAllSubsciptions,
  isMobileNumberProvided,
  onCloseAttempt,
  client,
  mobileSubscriptions,
  isNewNumber,
  mobileNumber,
  isMovingDateProvided,
  choosenNetwork,
  lang,
  handleNext,
  account,
  selected
}) => {
  const [formNetwork, setFormNetwork] = useState(choosenNetwork);
  const [isLoading, setIsLoading] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(false);
  const [templateCharacteristic, setTemplateCharacteristic] = useState(() =>
    mapTemplateCharacteristic(userData?.fourthStep?.template)
  );
  const [activeDataSharingCard, setActiveDataSharingCard] = useState(1);
  const [dataSharingCards, setDataSharingCards] = useState(() =>
    [1, 2, 3].filter(
      cardNumber =>
        cardNumber === 1 ||
        userData?.fourthStep?.[`extra_card_${cardNumber}_added`] === true ||
        isDefined(
          userData?.fourthStep?.[`extra_card_${cardNumber}_activate_esim`]
        ) ||
        isDefined(
          userData?.fourthStep?.[`extra_card_${cardNumber}_sim_card_number`]
        )
    )
  );

  useEffect(() => {
    if (
      (isMobileNumberProvided || isNewNumber) &&
      isUndefined(mobileSubscriptions)
    ) {
      const accountReference = account?.externalReference;
      getAllSubsciptions({
        lang,
        clientId: client,
        setIsLoading,
        accountReference
      });
    }
    // eslint-disable-next-line
  }, [
    client,
    isMobileNumberProvided,
    isNewNumber,
    account,
    mobileSubscriptions
  ]);

  const fetchTemplate = useCallback(
    async (subscriptionValue, setFieldValue) => {
      setIsTemplateLoading(true);
      setTemplateError(false);
      setFieldValue('template', undefined);
      setTemplateCharacteristic({});
      try {
        const response = await getData(
          `${URLS.QUOTE_CLIENTS}/${client}/products/${subscriptionValue}/templates?account_reference=${account?.externalReference}`
        );
        const fetchedTemplate = Optional(response?.[0]).or({});
        if (isUndefined(fetchedTemplate?.code)) {
          showSnackbar({
            intlTextKey: 'MESSAGE.NEW_USER_REQUESTS_TEMPLATE_ERROR',
            messageType: 'error'
          });
          setTemplateError(true);
        } else {
          setFieldValue('template', fetchedTemplate);
          setTemplateCharacteristic(mapTemplateCharacteristic(fetchedTemplate));
        }
      } catch {}
      setIsTemplateLoading(false);
    },
    // eslint-disable-next-line
    [client, account]
  );

  const onSubmit = useCallback(
    dialogValues => {
      const fullSubObject = mobileSubscriptions.find(
        sub => sub.value === dialogValues.mobile_subscription
      );
      handleNext();
      setUserData({ ...dialogValues, fullSubObject }, 'fourthStep');
    },
    // eslint-disable-next-line
    [userData, mobileSubscriptions]
  );

  const onBackClick = useCallback(
    () => {
      const stepSize = selected === 'port' && isDefined(mobileNumber) ? 1 : 2;
      setUserData(null, 'fourthStep');
      handleBack(stepSize);
    },
    // eslint-disable-next-line
    []
  );

  return (
    <Formik
      initialValues={{
        ...userData.fourthStep,
        isMobileNumberProvided,
        isNewNumber,
        mobileNumber,
        isMovingDateProvided,
        activate_esim: isUndefined(userData?.fourthStep?.activate_esim)
          ? true
          : userData.fourthStep.activate_esim,
        have_mobile_sim_card:
          userData?.fourthStep?.have_mobile_sim_card === true,
        data_sharing_card_activate_esim: isUndefined(
          userData?.fourthStep?.data_sharing_card_activate_esim
        )
          ? userData?.fourthStep?.data_sharing_card === true
          : userData.fourthStep.data_sharing_card === true &&
            userData.fourthStep.data_sharing_card_activate_esim === true,
        have_data_sharing_sim_card:
          userData?.fourthStep?.have_data_sharing_sim_card === true,
        extra_card_1_activate_esim: isUndefined(
          userData?.fourthStep?.extra_card_1_activate_esim
        )
          ? userData?.fourthStep?.data_sharing_card_activate_esim === true
          : userData.fourthStep.extra_card_1_activate_esim,
        template: userData.fourthStep?.template
      }}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmit}
      validationSchema={validateSchema({ formNetwork }).subscriptionStep}
    >
      {({
        handleSubmit,
        errors,
        values,
        handleBlur,
        handleChange,
        setFieldValue,
        setFieldError
      }) => {
        const fieldProps = {
          errors,
          values,
          handleBlur,
          handleChange
        };

        const numberInputAdornment = Optional(
          PROVIDERS_NUMBERS[values.network]
        ).or('');

        const setHaveSimcards = (value, field) => {
          const isChecked = value.target.checked;

          if (field === 'have_mobile_sim_card') {
            setFieldValue('mobile_sim_card_number', '');

            if (isChecked) {
              setFieldValue('activate_esim', false);
            } else if (values.activate_esim !== true) {
              return setFieldValue('activate_esim', true);
            }
          }

          if (field === 'activate_esim') {
            if (isChecked) {
              setFieldValue('have_mobile_sim_card', false);
              setFieldValue('mobile_sim_card_number', '');
            } else if (values.have_mobile_sim_card !== true) {
              return setFieldValue('activate_esim', true);
            }
          }

          if (field === 'have_data_sharing_sim_card') {
            setFieldValue('extra_card_1_sim_card_number', '');

            if (isChecked) {
              setFieldValue('data_sharing_card_activate_esim', false);
              setFieldValue('extra_card_1_activate_esim', false);
            } else if (values.data_sharing_card_activate_esim !== true) {
              return setFieldValue('data_sharing_card_activate_esim', true);
            }
          }

          if (field === 'data_sharing_card_activate_esim') {
            if (isChecked) {
              setFieldValue('have_data_sharing_sim_card', false);
              setFieldValue('extra_card_1_sim_card_number', '');
              setFieldValue('extra_card_1_activate_esim', true);
            } else if (values.have_data_sharing_sim_card !== true) {
              return setFieldValue('data_sharing_card_activate_esim', true);
            }
          }

          if (field === 'data_sharing_card') {
            setFieldValue('extra_card_1_sim_card_number', '');
            setFieldValue('have_data_sharing_sim_card', false);
            setFieldValue('data_sharing_card_activate_esim', isChecked);
            setFieldValue('extra_card_1_activate_esim', isChecked);
          }

          return setFieldValue(field, isChecked);
        };

        const setSubscriptionValue = (field, value) => {
          const priceText = translate('ACTIVATION_SIMCARD.PRICE_TEXT', {
            ...findProductPrices(value)
          });
          const subscriptionNetwork = Optional(value?.specification?.network)
            .map(network => `${network} NET`)
            .or(void 0);

          if (isDefined(subscriptionNetwork)) {
            setFormNetwork(subscriptionNetwork);
            setFieldValue('network', subscriptionNetwork);
            setFieldValue('isDisabledNetworkSelect', true);
          } else {
            setFormNetwork('');
            setFieldValue('network', '');
            setFieldValue('isDisabledNetworkSelect', false);
          }

          if (formNetwork !== subscriptionNetwork) {
            setFieldValue('mobile_sim_card_number', '');
            [1, 2, 3].forEach(cardNumber =>
              setFieldValue(`extra_card_${cardNumber}_sim_card_number`, '')
            );
          }
          setFieldValue('priceText', priceText);
          setFieldValue(field, value.value);
          fetchTemplate(value.value, setFieldValue);
        };

        const onChangeIcc = (field, event) => {
          setFieldValue(field, event.target.value);
          setFieldError(field, '');
        };

        const simCardMode = values.send_sim_card
          ? 'esim_and_sim_card'
          : values.activate_esim
          ? 'esim'
          : 'sim_card';

        const onSimCardModeChange = event => {
          const mode = event.target.value;
          if (mode === 'esim') {
            setFieldValue('activate_esim', true);
            setFieldValue('send_sim_card', false);
            setFieldValue('have_mobile_sim_card', false);
            setFieldValue('mobile_sim_card_number', '');
          } else if (mode === 'sim_card') {
            setFieldValue('activate_esim', false);
            setFieldValue('send_sim_card', false);
            setFieldValue('have_mobile_sim_card', true);
          } else {
            setFieldValue('activate_esim', true);
            setFieldValue('send_sim_card', true);
            setFieldValue('have_mobile_sim_card', false);
          }
        };

        const dataSharingSimCardMode =
          values[`extra_card_${activeDataSharingCard}_send_sim_card`] ||
          (activeDataSharingCard === 1 && values.send_sim_card_sharing)
            ? 'esim_and_sim_card'
            : values[`extra_card_${activeDataSharingCard}_activate_esim`]
            ? 'esim'
            : 'sim_card';

        const onDataSharingSimCardModeChange = event => {
          const mode = event.target.value;
          const activateEsimField = `extra_card_${activeDataSharingCard}_activate_esim`;
          const haveSimCardField = `extra_card_${activeDataSharingCard}_have_sim_card`;
          const sendSimCardField = `extra_card_${activeDataSharingCard}_send_sim_card`;
          const simCardNumberField = `extra_card_${activeDataSharingCard}_sim_card_number`;
          if (mode === 'esim') {
            setFieldValue(activateEsimField, true);
            setFieldValue(haveSimCardField, false);
            setFieldValue(sendSimCardField, false);
            setFieldValue(simCardNumberField, '');
          } else if (mode === 'sim_card') {
            setFieldValue(activateEsimField, false);
            setFieldValue(haveSimCardField, true);
            setFieldValue(sendSimCardField, false);
          } else {
            setFieldValue(activateEsimField, true);
            setFieldValue(haveSimCardField, false);
            setFieldValue(sendSimCardField, true);
          }

          if (activeDataSharingCard === 1) {
            setFieldValue(
              'data_sharing_card_activate_esim',
              mode !== 'sim_card'
            );
            setFieldValue(
              'send_sim_card_sharing',
              mode === 'esim_and_sim_card'
            );
            setFieldValue('have_data_sharing_sim_card', mode === 'sim_card');
          }
        };

        const simCardValues = {
          activate_esim: simCardMode === 'esim',
          send_sim_card: simCardMode === 'esim_and_sim_card',
          have_mobile_sim_card: simCardMode === 'sim_card'
        };

        const dataSharingSimCardValues = {
          [`extra_card_${activeDataSharingCard}_activate_esim`]:
            dataSharingSimCardMode === 'esim',
          [`extra_card_${activeDataSharingCard}_have_sim_card`]:
            dataSharingSimCardMode === 'sim_card',
          [`extra_card_${activeDataSharingCard}_send_sim_card`]:
            dataSharingSimCardMode === 'esim_and_sim_card',
          send_sim_card_sharing:
            activeDataSharingCard === 1 &&
            dataSharingSimCardMode === 'esim_and_sim_card'
        };
        const activeDataSharingCardCharacteristics =
          templateCharacteristic.dataSharingCards?.[
            activeDataSharingCard - 1
          ] || {};

        if (values.have_mobile_sim_card || values.have_data_sharing_sim_card) {
          values.shipping_address = '';
        }

        return (
          <Form onSubmit={handleSubmit} className={classes.root}>
            {(isMobileNumberProvided || isNewNumber) && (
              <div className={classes.sectionContainer}>
                {isMobileNumberProvided && (
                  <InputRow
                    name="mobileNumber"
                    label="REQUEST_NEW_USER.MOBILE"
                    disabled={true}
                    {...fieldProps}
                  />
                )}
                <div className={classes.rowContainer}>
                  {doesArrayHasLength(mobileSubscriptions) || isLoading ? (
                    <div>
                      <SelectRow
                        name="mobile_subscription"
                        label="REQUEST_NEW_USER.MOBILE_SUBSCRIPTION"
                        isEditModal={false}
                        options={mobileSubscriptions || []}
                        setFieldValue={(field, value) =>
                          setSubscriptionValue(field, value)
                        }
                        isLoading={isLoading}
                        isRequired={true}
                        returnFullSelectedObject={true}
                        {...fieldProps}
                      />
                      <div
                        className={clsx(classes.pricesContainer, {
                          [classes.hidden]: isUndefined(
                            values.mobile_subscription
                          )
                        })}
                      >
                        <div>{values.priceText}</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <InputRow
                        name="mobile_subscription"
                        label="REQUEST_NEW_USER.MOBILE_SUBSCRIPTION"
                        isRequired={true}
                        {...fieldProps}
                      />
                      <div
                        className={clsx(classes.pricesContainer, {
                          [classes.hidden]: isUndefined(
                            values.mobile_subscription
                          )
                        })}
                      >
                        <div>{values.priceText}</div>
                      </div>
                    </div>
                  )}

                  <div className={classes.simCardContainer}>
                    {values.network.indexOf('TDC') !== -1 ||
                    values.network.indexOf('3 NET') !== -1 ? (
                      <>
                        <CheckboxRow
                          label="ACTIVATE_ESIM"
                          name="activate_esim"
                          {...fieldProps}
                          values={simCardValues}
                          disabled={
                            isStringEmpty(values.network) ||
                            !templateCharacteristic.hasActivateEsim
                          }
                          customStyles={CUSTOM_STYLES.simCardCheckbox}
                          customCheckFunction={() =>
                            onSimCardModeChange({ target: { value: 'esim' } })
                          }
                        />
                        <CheckboxRow
                          label="REQUEST_NEW_USER.ESIM_AND_SIM_CARD"
                          name="send_sim_card"
                          {...fieldProps}
                          values={simCardValues}
                          disabled={
                            isStringEmpty(values.network) ||
                            !templateCharacteristic.hasActivateEsim ||
                            !templateCharacteristic.hasPrimarySimCard
                          }
                          customStyles={CUSTOM_STYLES.simCardCheckbox}
                          customCheckFunction={() =>
                            onSimCardModeChange({
                              target: { value: 'esim_and_sim_card' }
                            })
                          }
                          infoIcon={true}
                          infoIconTooltipIntlKey="TOOLTIP.ESIM_AND_SIM_CARD"
                        />
                        <CheckboxRow
                          label="REQUEST_NEW_USER.HAVE_SIMCARD"
                          name="have_mobile_sim_card"
                          {...fieldProps}
                          values={simCardValues}
                          disabled={
                            isStringEmpty(values.network) ||
                            !templateCharacteristic.hasPrimarySimCard
                          }
                          customStyles={CUSTOM_STYLES.simCardCheckbox}
                          customCheckFunction={() =>
                            onSimCardModeChange({
                              target: { value: 'sim_card' }
                            })
                          }
                        />
                      </>
                    ) : (
                      <CheckboxRow
                        label="REQUEST_NEW_USER.HAVE_SIMCARD"
                        name="have_mobile_sim_card"
                        disabled={
                          isStringEmpty(values.network) ||
                          !templateCharacteristic.hasPrimarySimCard
                        }
                        customStyles={CUSTOM_STYLES.simCardCheckbox}
                        customCheckFunction={value =>
                          setHaveSimcards(value, 'have_mobile_sim_card')
                        }
                        {...fieldProps}
                      />
                    )}

                    {simCardMode === 'sim_card' && (
                      <>
                        <InputRow
                          name="mobile_sim_card_number"
                          label="REQUEST_NEW_USER.SIM_NUMBER"
                          inputAdornmentText={numberInputAdornment}
                          isRequired={true}
                          disabled={isStringEmpty(values.network)}
                          customOnChange={event =>
                            onChangeIcc('mobile_sim_card_number', event)
                          }
                          {...fieldProps}
                        />
                        <ValidateEuicc
                          name="mobile_sim_card_number"
                          setFieldValue={setFieldValue}
                          disabled={isStringEmpty(values.network)}
                          shouldReset={!values.have_mobile_sim_card}
                          network={values.network}
                          {...fieldProps}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className={classes.rowContainer}>
                  <InputRow
                    name="network"
                    label="TABLE.NETWORK"
                    disabled={true}
                    {...fieldProps}
                  />
                </div>
                {templateCharacteristic.hasRefill && (
                  <CheckboxRow
                    label="DATA_LIMITS.AUTO_RECHARGE"
                    name="allow_recharge"
                    customStyles={CUSTOM_STYLES.checkbox}
                    {...fieldProps}
                  />
                )}
                {values.network.indexOf('TDC') !== -1 &&
                  (templateCharacteristic.hasDataSharingEsim ||
                    templateCharacteristic.hasDataSharingSimCard) && (
                    <div
                      className={clsx(
                        classes.rowContainer,
                        classes.sharingCard
                      )}
                    >
                      <CheckboxRow
                        label="REQUEST_NEW_USER.DATA_CARD"
                        name="data_sharing_card"
                        customStyles={CUSTOM_STYLES.checkbox}
                        disabled={
                          values.network === '3 NET' ||
                          isStringEmpty(values.network)
                        }
                        customCheckFunction={value =>
                          setHaveSimcards(value, 'data_sharing_card')
                        }
                        {...fieldProps}
                      />
                      <div
                        className={classes.simCardContainer}
                        style={
                          !values.data_sharing_card
                            ? { visibility: 'hidden' }
                            : undefined
                        }
                      >
                        <div className={classes.dataSharingTabsContainer}>
                          <Tabs
                            value={activeDataSharingCard}
                            onChange={(event, cardNumber) =>
                              setActiveDataSharingCard(cardNumber)
                            }
                            className={classes.dataSharingTabs}
                          >
                            {dataSharingCards.map(cardNumber => (
                              <Tab
                                key={cardNumber}
                                value={cardNumber}
                                label={`Extra ${cardNumber}`}
                                className={classes.dataSharingTab}
                              />
                            ))}
                          </Tabs>
                          {dataSharingCards.length < 3 && (
                            <Tooltip
                              title={translate('TOOLTIP.ADD_EXTRA_SIM_CARD')}
                            >
                              <span>
                                <SecondaryButton
                                  aria-label="Add extra SIM card"
                                  customStyles={{
                                    minWidth: '36px',
                                    marginBottom: '10px'
                                  }}
                                  disabled={
                                    !templateCharacteristic.dataSharingCards?.[
                                      dataSharingCards.length
                                    ]?.hasEsim &&
                                    !templateCharacteristic.dataSharingCards?.[
                                      dataSharingCards.length
                                    ]?.hasSimCard
                                  }
                                  onClick={() => {
                                    const cardNumber =
                                      dataSharingCards.length + 1;
                                    const card =
                                      templateCharacteristic.dataSharingCards?.[
                                        cardNumber - 1
                                      ];
                                    setFieldValue(
                                      `extra_card_${cardNumber}_activate_esim`,
                                      card?.hasEsim
                                    );
                                    setFieldValue(
                                      `extra_card_${cardNumber}_have_sim_card`,
                                      !card?.hasEsim
                                    );
                                    setFieldValue(
                                      `extra_card_${cardNumber}_added`,
                                      true
                                    );
                                    setDataSharingCards([
                                      ...dataSharingCards,
                                      cardNumber
                                    ]);
                                    setActiveDataSharingCard(cardNumber);
                                  }}
                                >
                                  <i className="fas fa-plus" />
                                </SecondaryButton>
                              </span>
                            </Tooltip>
                          )}
                          {dataSharingCards.length > 1 && (
                            <Tooltip
                              title={translate('TOOLTIP.REMOVE_EXTRA_SIM_CARD')}
                            >
                              <span>
                                <SecondaryButton
                                  aria-label={`Remove extra ${
                                    dataSharingCards[
                                      dataSharingCards.length - 1
                                    ]
                                  }`}
                                  customStyles={{
                                    minWidth: '36px',
                                    marginBottom: '10px'
                                  }}
                                  onClick={() => {
                                    const cardNumber =
                                      dataSharingCards[
                                        dataSharingCards.length - 1
                                      ];
                                    [
                                      'added',
                                      'activate_esim',
                                      'have_sim_card',
                                      'send_sim_card',
                                      'sim_card_number'
                                    ].forEach(field =>
                                      setFieldValue(
                                        `extra_card_${cardNumber}_${field}`,
                                        undefined
                                      )
                                    );
                                    setDataSharingCards(
                                      dataSharingCards.filter(
                                        card => card < cardNumber
                                      )
                                    );
                                    if (activeDataSharingCard === cardNumber) {
                                      setActiveDataSharingCard(cardNumber - 1);
                                    }
                                  }}
                                >
                                  <i className="fas fa-times" />
                                </SecondaryButton>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        {activeDataSharingCardCharacteristics.hasEsim && (
                          <CheckboxRow
                            label="ACTIVATE_ESIM"
                            name={`extra_card_${activeDataSharingCard}_activate_esim`}
                            {...fieldProps}
                            values={dataSharingSimCardValues}
                            disabled={isStringEmpty(values.network)}
                            customStyles={CUSTOM_STYLES.simCardCheckbox}
                            customCheckFunction={() =>
                              onDataSharingSimCardModeChange({
                                target: { value: 'esim' }
                              })
                            }
                          />
                        )}
                        {activeDataSharingCardCharacteristics.hasEsim &&
                          activeDataSharingCardCharacteristics.hasSimCard && (
                            <CheckboxRow
                              label="REQUEST_NEW_USER.ESIM_AND_SIM_CARD"
                              name={`extra_card_${activeDataSharingCard}_send_sim_card`}
                              {...fieldProps}
                              values={dataSharingSimCardValues}
                              disabled={isStringEmpty(values.network)}
                              customStyles={CUSTOM_STYLES.simCardCheckbox}
                              customCheckFunction={() =>
                                onDataSharingSimCardModeChange({
                                  target: { value: 'esim_and_sim_card' }
                                })
                              }
                              infoIcon={true}
                              infoIconTooltipIntlKey="TOOLTIP.ESIM_AND_SIM_CARD"
                            />
                          )}
                        {activeDataSharingCardCharacteristics.hasSimCard && (
                          <CheckboxRow
                            label="REQUEST_NEW_USER.HAVE_SIMCARD"
                            name={`extra_card_${activeDataSharingCard}_have_sim_card`}
                            {...fieldProps}
                            values={dataSharingSimCardValues}
                            disabled={isStringEmpty(values.network)}
                            customStyles={CUSTOM_STYLES.simCardCheckbox}
                            customCheckFunction={() =>
                              onDataSharingSimCardModeChange({
                                target: { value: 'sim_card' }
                              })
                            }
                          />
                        )}
                        {dataSharingSimCardMode === 'sim_card' && (
                          <>
                            <InputRow
                              label="REQUEST_NEW_USER.SIM_NUMBER"
                              name={`extra_card_${activeDataSharingCard}_sim_card_number`}
                              inputAdornmentText={numberInputAdornment}
                              isRequired={true}
                              disabled={isStringEmpty(values.network)}
                              customOnChange={event => {
                                onChangeIcc(
                                  `extra_card_${activeDataSharingCard}_sim_card_number`,
                                  event
                                );
                                if (activeDataSharingCard === 1) {
                                  setFieldValue(
                                    'data_sharing_sim_card_number',
                                    event.target.value
                                  );
                                }
                              }}
                              {...fieldProps}
                            />
                            <ValidateEuicc
                              name={`extra_card_${activeDataSharingCard}_sim_card_number`}
                              setFieldValue={setFieldValue}
                              disabled={isStringEmpty(values.network)}
                              shouldReset={
                                dataSharingSimCardMode !== 'sim_card'
                              }
                              network={values.network}
                              {...fieldProps}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}

            <MandatoryField />
            <div className={classes.footer}>
              <PrimaryButton onClick={onBackClick}>
                {translate('BUTTON.BACK')}
              </PrimaryButton>
              <PrimaryButton
                onClick={handleSubmit}
                disabled={isLoading || isTemplateLoading || templateError}
              >
                {translate('BUTTON.NEXT')}
              </PrimaryButton>
              <SecondaryButton onClick={onCloseAttempt || resestUserWizard}>
                {translate('BUTTON.CANCEL')}
              </SecondaryButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapStatesToProps = ({
  requestUserWizard: { userData, mobileSubscriptions, checkNumberData },
  selectClient,
  auth
}) => {
  const { firstStep, secondStep, thirdStep, fourthStep } = userData;
  const isMovingDateProvided =
    isDefined(thirdStep.move_number_date) || thirdStep.is_old_subscripion_date;
  const isNumberOur = Optional(checkNumberData.our)
    .map(our => our)
    .or(false);
  const mobileNumber = secondStep.mobile_number;
  const isMobileNumberProvided = isStringDefined(secondStep.mobile_number);
  const isNewNumber = secondStep.isNewNumber;
  const client = Optional(selectClient.selectedClient.id).or(
    auth.defaultClient.id
  );
  const choosenNetwork = fourthStep.network;
  const account = firstStep.account;
  const selected = secondStep.selected;
  return {
    userData,
    isMobileNumberProvided,
    isNumberOur,
    isNewNumber,
    client,
    mobileNumber,
    mobileSubscriptions,
    isMovingDateProvided,
    choosenNetwork,
    lang: auth.lang,
    account,
    selected
  };
};

const mapDispatchToProps = {
  ...reduxActions,
  getAllSubsciptions
};

export default flowRight(
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(SubscriptionStep);
